import { useJobProgress } from '../models/jobs/queries';

/**
 * Hook for determining job status and derived flags for a search query.
 * Shared by LeadDataContext and LeadJobContext; React Query prevents duplicate HTTP requests.
 *
 * @param {string|null} selectedSearchQueryId - ID of the selected search query
 * @returns {Object} Job status and flags
 * @returns {Array<Object>} returns.runningJobs - All running/queued jobs
 * @returns {boolean} returns.hasRunningCompanyScraperJob - Whether a company scraper job is running or queued
 * @returns {boolean} returns.hasCancellingCompanyScraperJob - Whether a company scraper job is being cancelled (but not yet finished)
 * @returns {boolean} returns.isCompanyScraperJobActive - Whether a company scraper job is active (running or cancelling)
 * @returns {boolean} returns.hasRunningAnalysisJob - Whether an AI analysis job is running
 * @returns {boolean} returns.hasRunningEnrichmentJob - Whether a contact enrichment job is running
 * @returns {boolean} returns.hasCancellingEnrichmentJob - Whether a contact enrichment job is being cancelled
 * @returns {Object<string, Object>} returns.runningJobsByType - Map of job type to job object
 */
export const useJobStatus = (selectedSearchQueryId) => {
	const { data: runningJobs = [] } = useJobProgress(selectedSearchQueryId);

	// Helper to check if job type is a company scraper
	const isCompanyScraperJob = (jobType) => jobType === 'googleMapsScraper' || jobType === 'localHrSearch';

	// Calculate job status flags
	const hasRunningCompanyScraperJob = runningJobs.some(
		(job) => isCompanyScraperJob(job.jobType) && (job.status === 'queued' || job.status === 'running'),
	);

	// Cancelling = aborted but processor not yet finished (no finishedAt)
	// Only if no new running job exists (prevents stale aborted job from affecting new job's UI)
	const hasCancellingCompanyScraperJob =
		!hasRunningCompanyScraperJob &&
		runningJobs.some((job) => isCompanyScraperJob(job.jobType) && job.status === 'aborted' && !job.finishedAt);

	// Combined: Job is "active" if running OR cancelling (keep realtime subscription alive)
	const isCompanyScraperJobActive = hasRunningCompanyScraperJob || hasCancellingCompanyScraperJob;

	const hasRunningAnalysisJob = runningJobs.some(
		(job) => job.jobType === 'companyAnalysis' && (job.status === 'queued' || job.status === 'running'),
	);

	const hasRunningEnrichmentJob = runningJobs.some(
		(job) => job.jobType === 'contactEnrichment' && (job.status === 'queued' || job.status === 'running'),
	);

	const hasCancellingEnrichmentJob =
		!hasRunningEnrichmentJob &&
		runningJobs.some((job) => job.jobType === 'contactEnrichment' && job.status === 'aborted' && !job.finishedAt);

	const runningJobsByType = runningJobs.reduce((acc, job) => {
		acc[job.jobType] = job;
		return acc;
	}, {});

	return {
		runningJobs,
		hasRunningCompanyScraperJob,
		hasCancellingCompanyScraperJob,
		isCompanyScraperJobActive,
		hasRunningAnalysisJob,
		hasRunningEnrichmentJob,
		hasCancellingEnrichmentJob,
		runningJobsByType,
	};
};
